import { Heading, Text, Avatar, IntlDateTime } from "@govlaunch/web";
import Box, { BoxProps } from "@govlaunch/web/src/box/Box";
import Flex from "@govlaunch/web/src/box/Flex";
import React, { ReactNode } from "react";
import { IProductFieldsFragment } from "../fragments/__generated__/ProductFieldsFragment.generated";
import SponsorshipBadges from "./SponsorshipBadges";
import { FormattedMessage } from "react-intl";

interface IProductVendorCardProps extends BoxProps {
  product: IProductFieldsFragment;
  renderLinkWrap?: (vendor: NonNullable<IProductFieldsFragment["company"]>, children: ReactNode) => ReactNode;
}

export default function ProductVendorCard({ product, renderLinkWrap, ...props }: IProductVendorCardProps) {
  if (!product.company) {
    return null;
  }

  const avatar = (
    <Avatar
      src={product.company.logo}
      name={product.company.name}
      size="lg"
      variant={product.company.isDisruptor ? "disruptor-without-icon" : undefined}
    />
  );

  const avatarWithBadges = (
    <Flex alignItems="center" justifyContent="center">
      <Box position="relative">
        {renderLinkWrap ? renderLinkWrap(product.company, avatar) : avatar}

        {product.company.sponsorships && <SponsorshipBadges sponsorships={product.company.sponsorships} size="lg" />}
      </Box>
    </Flex>
  );

  const name = (
    <Heading as="h2" size="md" color="blue.600">
      {product.company.name}
    </Heading>
  );

  return (
    <Box {...props} bg="teal.100" p={4} textAlign="center">
      {avatarWithBadges}

      <Box mt={4}>
        {renderLinkWrap ? renderLinkWrap(product.company, name) : name}

        {product.company.yearFounded && (
          <Text mt={2} fontSize="sm" color="gray.600" fontWeight="semibold">
            <FormattedMessage
              defaultMessage="Founded in {yearFounded}" id="RAjY63"
              values={{ yearFounded: product.company.yearFounded }}
            />
          </Text>
        )}

        {product.company.description && (
          <Text mt={2} fontSize="xs" color="gray.500" fontWeight="normal">
            {product.company.description}
          </Text>
        )}

        {product.company.createdAt && (
          <Text mt={2} fontSize="sm" color="gray.600" fontWeight="normal">
            <FormattedMessage defaultMessage="On Govlaunch since" id="b05iu8" /> <IntlDateTime value={product.company.createdAt} />
          </Text>
        )}
      </Box>
    </Box>
  );
}
